import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { CurrencySymbolPosition } from '@ConfigLoader/Configs/CurrenciesConfig';
import { ICurrencyValue } from '@GameStore/GameStoreSchema';

export interface CurrencyUtilsConfig {
    finalDivider: number;
    exponent: number;
    symbol: string;
    symbolPosition: CurrencySymbolPosition;
    symbolSeparator: string;
    decimalSeparator: string;
    withSymbol: boolean;
    currencyHeaderLabel: boolean | string;
    hideDecimals?: boolean;
}

export class CurrencyUtils {
    public static readonly SYMBOL_POSITION_DEFAULT = CurrencySymbolPosition.PREFIX;
    public static readonly SYMBOL_SEPARATOR_DEFAULT = ' ';
    public static readonly DECIMAL_SEPARATOR_DEFAULT = ',';
    public static readonly CURRENCY_HEADER_LABEL_DEFAULT = false;
    private static config: CurrencyUtilsConfig;
    private static iso: string;

    public static init(finalDivider: number, exponent: number, iso: string) {
        const {
            symbol = iso,
            symbolPosition = CurrencyUtils.SYMBOL_POSITION_DEFAULT,
            symbolSeparator = CurrencyUtils.SYMBOL_SEPARATOR_DEFAULT,
            decimalSeparator = CurrencyUtils.DECIMAL_SEPARATOR_DEFAULT,
            currencyHeaderLabel = CurrencyUtils.CURRENCY_HEADER_LABEL_DEFAULT
        } = ConfigLoader.getConfiguration(EConfigurationNames.CURRENCIES)[iso] || {};
        CurrencyUtils.iso = iso;
        CurrencyUtils.config = {
            finalDivider,
            exponent,
            symbol,
            symbolPosition,
            symbolSeparator,
            decimalSeparator,
            withSymbol: true,
            currencyHeaderLabel,
            hideDecimals: false
        };
    }

    public static getCurrencySymbol() {
        return this.config.symbol;
    }

    public static getCurrencyISO() {
        return this.iso;
    }

    public static formatRawCurrencyValue(rawCurrencyValue: number, config?: Partial<CurrencyUtilsConfig>): string {
        const finalConfig = CurrencyUtils.mergeConfig(config);
        const normalized = CurrencyUtils.normalizeRawCurrencyValue(rawCurrencyValue, finalConfig);
        return CurrencyUtils.formatValue(normalized, finalConfig);
    }

    public static formatRawPaytableValue(
        rawBetValue: number,
        rawPaytableMultiplierValue: number,
        isRolesPaytable: boolean,
        config?: Partial<CurrencyUtilsConfig>
    ): string {
        const finalConfig = CurrencyUtils.mergeConfig(config);
        const normalized =
            CurrencyUtils.normalizeRawCurrencyValue(rawBetValue, finalConfig) *
            CurrencyUtils.normalizeRawPaytableMultiplierValue(rawPaytableMultiplierValue, isRolesPaytable, finalConfig);
        return CurrencyUtils.formatValue(normalized, finalConfig);
    }

    public static createCurrencyValue(rawCurrencyValue: number, config?: Partial<CurrencyUtilsConfig>): ICurrencyValue {
        const finalConfig = CurrencyUtils.mergeConfig(config);
        return {
            raw: rawCurrencyValue,
            normalized: CurrencyUtils.normalizeRawCurrencyValue(rawCurrencyValue, finalConfig),
            formatted: CurrencyUtils.formatRawCurrencyValue(rawCurrencyValue, finalConfig)
        };
    }

    public static getConfig(): CurrencyUtilsConfig {
        if (!CurrencyUtils.config) {
            throw new Error('[CurrencyUtils] Config not initialized. Call CurrencyUtils.init() before using any currency formatting methods.');
        }
        return CurrencyUtils.config;
    }

    private static normalizeRawCurrencyValue(rawCurrencyValue: number, config: CurrencyUtilsConfig): number {
        return rawCurrencyValue / 10 ** config.exponent;
    }

    private static normalizeRawPaytableMultiplierValue(rawMultiplierValue: number, isRolesPaytable: boolean, config: CurrencyUtilsConfig): number {
        if (isRolesPaytable) return rawMultiplierValue;
        return rawMultiplierValue / config.finalDivider;
    }

    private static formatValue(normalizedValue: number, config: CurrencyUtilsConfig): string {
        const formatted = normalizedValue.toFixed(config.hideDecimals ? 0 : config.exponent).replace('.', config.decimalSeparator);
        return config.withSymbol ? CurrencyUtils.applyCurrencySymbol(formatted, config) : formatted;
    }

    private static applyCurrencySymbol(formattedValue: string, config: CurrencyUtilsConfig): string {
        return config.symbolPosition === CurrencySymbolPosition.SUFFIX
            ? `${formattedValue}${config.symbolSeparator}${config.symbol}`
            : `${config.symbol}${config.symbolSeparator}${formattedValue}`;
    }

    private static mergeConfig(config?: Partial<CurrencyUtilsConfig>): CurrencyUtilsConfig {
        return { ...CurrencyUtils.getConfig(), ...config };
    }
}
